// ---------------------------------------------------------------------------
// Telemetria passiva (overlay de diagnóstico) — só existe com "?dbg=1" na URL.
//
// Sem a flag, DBG_ENABLED é false e TODAS as funções abaixo retornam na primeira
// linha: nada é guardado, nenhum listener é criado, nenhum cálculo extra roda.
// Nada aqui altera navegação, escala, fetch, clipping ou overscan: só LÊ valores
// que o app já calcula e os guarda em buffers circulares de tamanho fixo.
// ---------------------------------------------------------------------------

import { DBG_ENABLED } from './dbgFlag';
import { markEvent, EV, jank } from './jankRecorder';
import { p1ScaleStep, p1LoadStart, p1LoadStage, p1LoadNewer, p1LoadDone, p1LoadCommit, S as P1S } from './p1Probe';
const jankSetMathCtx = (c: boolean) => jank?.setMathCtx(c);
const jankInCargaMath = () => !!jank?.inCargaMath;
export { DBG_ENABLED };

const MAX_EVENTS = 60;
const MAX_LOADS = 60;
const MAX_MATH = 60;
const MAX_LOAD_TIMES = 2000; // carimbos de tempo das cargas (para cargas/s mesmo em laço rápido)
const MAX_FPS = 180;        // amostras de FPS (1 por segundo)

export interface DbgEvent { t: number; text: string }
export interface DbgLoad {
  id: number;
  t: number;            // quando terminou (performance.now)
  ended: boolean;       // fim do histórico / erro (sem velas novas)
  reason?: string;
  fetched?: number;
  endTime?: number;     // timestamp de abertura da vela mais antiga resultante
  total?: number;
  netMs?: number;
  procMs?: number;      // processRawBinanceData (thread principal)
  totalMs?: number;
  commitMs?: number;    // setRealData -> efeito pós-commit do overlay
  repeated?: boolean;   // mesmo endTime da carga anterior
  failed?: string;      // F1 C3: a carga FALHOU (tipo do erro); não é fim do histórico
}
export interface DbgMathCall { t: number; ms: number; n: number }
export interface DbgScene {
  data: any[];
  minP: number; maxP: number;
  wallMinP: number; wallMaxP: number;
  wallStale: boolean;
  padLeft: number; padRight: number;
  sliceOffset: number; wallSliceOffset: number;
  /** índice ancorado da vela mais nova da série do lote (F2; NaN = sem etiqueta) */
  newestAbs?: number;
  visibleCount: number;
  showSMA10: boolean; showSMA20: boolean; showSMA50: boolean;
}

export const dbgState = {
  t0: 0,
  events: [] as DbgEvent[],
  loads: [] as DbgLoad[],
  loadTimes: [] as number[],
  fps: [] as { t: number; fps: number }[],
  math: [] as DbgMathCall[],
  scene: null as DbgScene | null,
  loadSeq: 0,
  loadStartT: 0,
  pendingCommit: null as { t: number; load: DbgLoad } | null,
  lastScaleJumpText: '—',
  scaleLog: [] as DbgScaleWindow[],
  /** início do último autoplay (retorno ao presente): o alvo cai para 0 por projeto, não é recuo */
  autoplayStartT: -Infinity,
  /** autoplay (retorno ao presente) em andamento: o alvo fica em 0 por projeto durante todo ele (não é recuo) */
  autoplayActive: false,
  /** última escrita do alvo pelo arrasto: quando, o que o dedo pediu e o que ficou (depois da trava da borda) */
  dragT: -Infinity, dragWanted: NaN, dragResult: NaN,
  /** F3a C4: janela deslizante de velas (tamanho, despejos, costuras, pré-carga, espera, heap por tamanho) */
  win: {
    limit: 0, len: 0, atPresent: true, tail: 0, startTime: null as number | null,
    evictOldN: 0, evictOldC: 0, evictNewN: 0, evictNewC: 0, stitches: 0, stitchC: 0, newerLoads: 0,
    aheadOlder: NaN, aheadNewer: NaN,
    waitN: 0, waitMs: 0, waitMax: 0,
    heapNowMB: NaN, heapPeakMB: NaN, heapPeakLen: 0, maxLen: 0,
    /** F3a.1: RECUOs sem arrasto (alvo diminuiu sem gesto): contagem, maior e os últimos (instante, de, para) */
    recuoN: 0, recuoMax: 0, recuos: [] as { t: number; from: number; to: number }[],
    /** F3d C6: queda do índice do alvo pela re-âncora da troca de série (a vista não anda): contada à parte */
    recuoTrocaN: 0, recuoTrocaMax: 0,
  },
};

/** F3a C4: estado da janela depois de cada entrega de dados (não por quadro). */
export function dbgWindowState(limit: number, len: number, atPresent: boolean, tail: number, startTime: number | null): void {
  if (!DBG_ENABLED) return;
  const W = dbgState.win;
  W.limit = limit; W.len = len; W.atPresent = atPresent; W.tail = tail; W.startTime = startTime;
  if (len > W.maxLen) W.maxLen = len;
}
export function dbgWindowEvict(side: 'oldest' | 'newest', count: number): void {
  if (!DBG_ENABLED) return;
  const W = dbgState.win;
  markEvent(EV.DESPEJO, count); // P1: evento para atribuição (marcas, pulos)
  if (side === 'oldest') { W.evictOldN++; W.evictOldC += count; } else { W.evictNewN++; W.evictNewC += count; }
}
export function dbgWindowStitch(tailLen: number): void { if (DBG_ENABLED) { markEvent(EV.COSTURA, tailLen); dbgState.win.stitches++; dbgState.win.stitchC += tailLen; } }
export function dbgWindowNewerLoad(): void { if (DBG_ENABLED) { dbgState.win.newerLoads++; p1LoadNewer(); } }
export function dbgWindowAhead(older: number, newer: number): void { if (DBG_ENABLED) { dbgState.win.aheadOlder = older; dbgState.win.aheadNewer = newer; } }
/** F3a.1: um RECUO sem arrasto (o alvo diminuiu sem gesto nem autoplay). */
export function dbgWindowRecuo(from: number, to: number): void {
  if (!DBG_ENABLED) return;
  const W = dbgState.win, d = from - to;
  W.recuoN++; if (d > W.recuoMax) W.recuoMax = d;
  push(W.recuos, { t: performance.now(), from, to }, 200);
}
/**
 * F3d C6: a re-âncora da troca de série (AnchoredNav.reanchor) baixa o índice ancorado do alvo em `newestAbs` velas
 * quando a janela está longe do presente; a distância do fim (a vista) não muda. Não é RECUO: contado à parte.
 */
export function dbgWindowRecuoTroca(drop: number): void {
  if (!DBG_ENABLED) return;
  const W = dbgState.win; W.recuoTrocaN++; if (drop > W.recuoTrocaMax) W.recuoTrocaMax = drop;
}
export function dbgWindowWait(ms: number): void {
  if (!DBG_ENABLED) return;
  const W = dbgState.win; W.waitN++; W.waitMs += ms; if (ms > W.waitMax) W.waitMax = ms;
}
/** heap total (performance.memory, aproximado no Chrome) contra o tamanho da janela; chamado a cada amostra de heap */
export function dbgWindowHeap(mb: number): void {
  if (!DBG_ENABLED || !(mb > 0)) return;
  const W = dbgState.win; W.heapNowMB = mb;
  if (!(mb <= W.heapPeakMB)) { W.heapPeakMB = mb; W.heapPeakLen = W.len; }
}
/** Topo do "Copiar resumo" (F3a): a janela em 4 linhas. */
export function windowSummary(): string[] {
  const W = dbgState.win, n = (v: number) => (Number.isFinite(v) ? Math.round(v).toLocaleString('pt-BR') : '—');
  const mb = (v: number) => (Number.isFinite(v) ? v.toFixed(1) + ' MB' : '—');
  return [
    `=== JANELA: limite ${n(W.limit)} velas | carregadas ${n(W.len)} (~${(W.len / 1000).toFixed(1)} blocos; máx ${n(W.maxLen)}) | presente na janela: ${W.atPresent ? 'sim' : 'NÃO'} | cauda ao vivo ${W.tail} ===`,
    `despejos: ${W.evictOldN + W.evictNewN} (${n(W.evictOldC)} mais antigas / ${n(W.evictNewC)} mais novas) | costuras: ${W.stitches} (${W.stitchC} velas da cauda) | cargas de blocos mais novos: ${W.newerLoads}`,
    `pré-carga à frente: passado ${n(W.aheadOlder)} / presente ${Number.isFinite(W.aheadNewer) ? n(W.aheadNewer) : 'na janela'} | espera por carga: ${W.waitN}x, total ${(W.waitMs / 1000).toFixed(1)} s, máx ${(W.waitMax / 1000).toFixed(1)} s`,
    `RECUOs sem arrasto: ${W.recuoN}${W.recuoN ? ` (maior ${W.recuoMax.toFixed(1)} velas)` : ''} | RECUOs por troca de série (excluídos): ${W.recuoTrocaN}${W.recuoTrocaN ? ` (maior ${W.recuoTrocaMax.toFixed(1)} velas)` : ''} | heap: ${mb(W.heapNowMB)} agora (pico ${mb(W.heapPeakMB)} com ${n(W.heapPeakLen)} velas) | início real dos dados: ${W.startTime === null ? 'não alcançado' : new Date(W.startTime).toISOString().slice(0, 16)}`,
  ];
}

/** Resumo de uma janela de 10 s da escala vertical (parede de fundo). */
export interface DbgScaleWindow {
  t: number;
  frames: number;
  occAvg: number; occMin: number;      // ocupação da altura pelas velas visíveis (%)
  stepMaxFrame: number;                // maior variação da faixa entre dois quadros (% da faixa)
  stepMaxPerSec: number;               // a mesma variação dividida pelo tempo do quadro (% da faixa por segundo)
  framesGe10: number;                  // quadros com variação >= 10%
  cutFrames: number;                   // quadros com vela visível fora da faixa desenhada
  calcN: number; calcAvgMs: number; calcMaxMs: number; // cálculo de escala (chamadas na janela)
}

// Acumulador da janela atual (campos mutáveis: nada é alocado por quadro)
const SW = { start: 0, frames: 0, occSum: 0, occMin: Infinity, stepMax: 0, stepPerSecMax: 0, ge10: 0, cut: 0,
  calcN: 0, calcSum: 0, calcMax: 0, prevMin: NaN, prevMax: NaN };
const SCALE_WINDOW_MS = 10_000;
if (DBG_ENABLED) dbgState.t0 = performance.now();

const push = <T,>(arr: T[], item: T, max: number) => { arr.push(item); if (arr.length > max) arr.shift(); };

export function dbgEvent(text: string): void {
  if (!DBG_ENABLED) return;
  push(dbgState.events, { t: performance.now(), text }, MAX_EVENTS);
}

/** O autoplay (retorno ao presente) começou: o alvo vai a 0 de propósito (não é RECUO). */
export function dbgAutoplayStart(): void {
  if (!DBG_ENABLED) return;
  dbgState.autoplayStartT = performance.now();
  dbgState.autoplayActive = true;
}
/** O autoplay terminou (chegou ao presente ou foi cancelado). */
export function dbgAutoplayEnd(): void {
  if (!DBG_ENABLED) return;
  dbgState.autoplayActive = false;
}
/** O arrasto acabou de gravar o alvo: `wanted` = alvo + movimento do dedo; `result` = depois da trava da borda. */
export function dbgDragWrite(wanted: number, result: number): void {
  dbgState.dragT = performance.now(); dbgState.dragWanted = wanted; dbgState.dragResult = result;
}
/**
 * O alvo caiu de `lastT` para `target` sem autoplay: é RECUO de verdade? Não é quando foi o próprio dedo que pediu
 * (o último movimento do arrasto grava o alvo e o gesto é solto antes do quadro seguinte). É quando o alvo ficou
 * MENOR do que o dedo pediu (trava da borda com o limite encolhendo) ou quando nada do arrasto explica a queda
 * (re-âncora pelo limite, por exemplo).
 */
export function isRealRecuo(lastT: number, target: number, sinceT: number): boolean {
  if (!(target < lastT - 0.5)) return false;
  const byDrag = dbgState.dragT >= sinceT && dbgState.dragResult === target;
  return !byDrag || target < dbgState.dragWanted - 0.5;
}

export function dbgLoadStart(): void {
  if (!DBG_ENABLED) return;
  markEvent(EV.CARGA_INICIO);
  p1LoadStart();
  dbgState.loadStartT = performance.now();
}

export function dbgLoadDone(info: Omit<DbgLoad, 'id' | 't' | 'repeated'>): void {
  if (!DBG_ENABLED) return;
  markEvent(EV.CARGA_FIM, info.totalMs ?? 0);
  p1LoadDone(!info.ended && !info.failed);
  const prev = dbgState.loads[dbgState.loads.length - 1];
  const load: DbgLoad = { ...info, id: ++dbgState.loadSeq, t: performance.now() };
  push(dbgState.loadTimes, load.t, MAX_LOAD_TIMES);
  load.repeated = !!(prev && !prev.ended && !prev.failed && !load.ended && !load.failed && prev.endTime !== undefined && prev.endTime === load.endTime);
  push(dbgState.loads, load, MAX_LOADS);
  if (load.failed) { dbgEvent(`carga #${load.id}: FALHA (${load.failed}) — sem velas novas; fim do histórico NÃO marcado`); return; }
  if (!load.ended) dbgState.pendingCommit = { t: performance.now(), load };
  const ago = (ms?: number) => (ms === undefined ? '?' : ms.toFixed(0));
  dbgEvent(load.ended
    ? `carga #${load.id}: SEM velas novas (${load.reason || 'fim'})`
    : `carga #${load.id}${load.repeated ? ' REPETIDA' : ''}: +${load.fetched} velas, total=${load.total}, mais antiga=${load.endTime ? new Date(load.endTime).toISOString().slice(0, 16) : '?'} | rede ${ago(load.netMs)}ms, proc ${ago(load.procMs)}ms (thread principal)`);
}

/** Chamado pelo efeito pós-commit do overlay quando os dados mudam. */
export function dbgCommitSeen(): void {
  if (!DBG_ENABLED) return;
  markEvent(EV.COMMIT_DADOS);
  const p = dbgState.pendingCommit;
  if (!p) return;
  p.load.commitMs = performance.now() - p.t;
  p1LoadCommit(p.load.commitMs);
  dbgState.pendingCommit = null;
}

/** Uma amostra de FPS por segundo, medida pelo overlay. */
export function dbgFps(fps: number): void {
  if (!DBG_ENABLED) return;
  push(dbgState.fps, { t: performance.now(), fps }, MAX_FPS);
}

/** Tempo de um cálculo de escala (qualquer implementação). */
export function dbgScaleCalc(ms: number): void {
  if (!DBG_ENABLED) return;
  SW.calcN++; SW.calcSum += ms; if (ms > SW.calcMax) SW.calcMax = ms;
}

/**
 * Chamado a cada quadro com a faixa de preço desenhada (dMin..dMax) e a faixa das velas visíveis
 * (vMin..vMax, pavios incluídos). A cada 10 s fecha uma janela, guarda o resumo e registra um evento.
 */
export function dbgScaleFrame(dMin: number, dMax: number, vMin: number, vMax: number, dtMs: number): void {
  if (!DBG_ENABLED) return;
  const now = performance.now();
  if (SW.start === 0) SW.start = now;
  const R = dMax - dMin;
  if (R > 0 && isFinite(R)) {
    if (SW.prevMax === SW.prevMax) {
      const R0 = SW.prevMax - SW.prevMin;
      const step = Math.max(Math.abs(dMax - SW.prevMax), Math.abs(dMin - SW.prevMin), Math.abs(R - R0)) / R0;
      if (step > SW.stepMax) SW.stepMax = step;
      const perSec = dtMs > 0 ? step / (dtMs / 1000) : 0;
      if (perSec > SW.stepPerSecMax) SW.stepPerSecMax = perSec;
      if (step >= 0.10) SW.ge10++;
      if (step >= 0.05) markEvent(EV.ESCALA_DEGRAU, step * 100);
      if (step >= 0.10) { markEvent(EV.DEGRAU10, step * 100); p1ScaleStep(step * 100, dtMs); } // P1 T5: lista de degraus >= 10%
    }
    SW.prevMin = dMin; SW.prevMax = dMax;
    if (vMax > vMin) {
      const occ = (vMax - vMin) / R;
      SW.occSum += occ; if (occ < SW.occMin) SW.occMin = occ;
      if (vMax > dMax + R * 1e-4 || vMin < dMin - R * 1e-4) SW.cut++;
    }
    SW.frames++;
  }
  if (now - SW.start >= SCALE_WINDOW_MS && SW.frames > 0) {
    const w: DbgScaleWindow = {
      t: now, frames: SW.frames,
      occAvg: (SW.occSum / SW.frames) * 100, occMin: SW.occMin === Infinity ? 0 : SW.occMin * 100,
      stepMaxFrame: SW.stepMax * 100, stepMaxPerSec: SW.stepPerSecMax * 100, framesGe10: SW.ge10, cutFrames: SW.cut,
      calcN: SW.calcN, calcAvgMs: SW.calcN ? SW.calcSum / SW.calcN : 0, calcMaxMs: SW.calcMax,
    };
    push(dbgState.scaleLog, w, MAX_EVENTS);
    dbgEvent(`escala 10s: ocupação média ${w.occAvg.toFixed(0)}% (mín ${w.occMin.toFixed(0)}%) | maior variação ${w.stepMaxFrame.toFixed(1)}%/quadro, ${w.stepMaxPerSec.toFixed(0)}%/s | quadros≥10%: ${w.framesGe10} | corte: ${w.cutFrames} | cálculo ${w.calcN}x média ${w.calcAvgMs.toFixed(3)}ms pico ${w.calcMaxMs.toFixed(3)}ms`);
    SW.start = now; SW.frames = 0; SW.occSum = 0; SW.occMin = Infinity; SW.stepMax = 0; SW.stepPerSecMax = 0; SW.ge10 = 0; SW.cut = 0;
    SW.calcN = 0; SW.calcSum = 0; SW.calcMax = 0;
  }
}

/** Contexto do processMathData em andamento: true = vindo de uma carga de histórico, false = tick ao vivo. */
export function dbgMathCtx(carga: boolean): void {
  if (!DBG_ENABLED) return;
  jankSetMathCtx(carga);
}

export function dbgMathCall(ms: number, n: number): void {
  if (!DBG_ENABLED) return;
  markEvent(jankInCargaMath() ? EV.MATH_CARGA : EV.MATH_TICK, ms);
  if (jankInCargaMath()) p1LoadStage(P1S.IND, ms);
  push(dbgState.math, { t: performance.now(), ms, n }, MAX_MATH);
}

/** Chamado no corpo do SceneContent (a cada render, NÃO a cada quadro). */
export function dbgScene(s: DbgScene): void {
  if (!DBG_ENABLED) return;
  const prev = dbgState.scene;
  if (prev && prev.sliceOffset !== s.sliceOffset) markEvent(EV.LOTE);
  if (prev && prev.wallSliceOffset !== s.wallSliceOffset) markEvent(EV.LOTE_PAREDE);
  if (prev && prev.sliceOffset !== s.sliceOffset) {
    const r0 = prev.maxP - prev.minP, r1 = s.maxP - s.minP;
    const pct = r0 > 0 ? ((r1 / r0) - 1) * 100 : 0;
    dbgState.lastScaleJumpText = `lote ${prev.sliceOffset}→${s.sliceOffset}: faixa do buffer ${r0.toFixed(2)}→${r1.toFixed(2)} (${pct >= 0 ? '+' : ''}${pct.toFixed(1)}%)`;
    dbgEvent(`re-slice ${dbgState.lastScaleJumpText} | buffer=${s.data.length} velas`);
  }
  dbgState.scene = s;
}
